import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TableSettings } from '@payer-admin-ui/components-v2/src/types/table';
import { AiSettings, UserSettings } from '@payer-admin-ui/components-v2/src/types/settings';
import { toPayerError } from 'utils/rtk';
import i18n from 'i18next';
import { MerchantAdminState } from './store';
import { userSettingsAPI } from './userSettingsAPI';
import { showSnackbar } from './snackbar';

let layoutSettingsSaveTimeoutId: number | null = null;
let aiSettingsSaveTimeoutId: number | null = null;

const defaultAiSettings: AiSettings = { openConversations: [] };

const initialState: UserSettings = {
  layoutSettings: { sidebarPinned: false, aiChatPanePinned: false, aiChatPaneOpen: false, aiChatPaneWidthPx: 360 },
  aiSettings: defaultAiSettings,
  tableSettings: {},
  isLoaded: false,
  app: 'MERCHANT_UI',
};

const defaultGridSettings: TableSettings = {
  density: 'standard',
  columnVisibility: {},
  columnWidths: {},
  appliedFilters: {},
  pageSize: 20,
  sortOrders: undefined,
};

export const loadUserSettings = createAsyncThunk<UserSettings, void, { state: MerchantAdminState }>(
  'userSettings/load',
  async (_, { dispatch }) => {
    const settings = await userSettingsAPI.getUserSettings();
    dispatch(setUserSettings(settings));
    return settings;
  },
);

export const saveUserSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'userSettings/save',
  async (_, { getState }) => {
    try {
      const state = getState();
      await userSettingsAPI.saveUserSettings(state.userSettings);
    } catch (error) {
      console.error('Error saving user settings', error);
    }
  },
);

export const updateUserSettings = createAsyncThunk<void, Partial<UserSettings>, { state: MerchantAdminState }>(
  'userSettings/update',
  async (update: Partial<UserSettings>, { getState, dispatch }) => {
    try {
      const state = getState().userSettings;
      dispatch(setUserSettings({ ...state, ...update }));
      dispatch(saveUserSettings());
    } catch (error) {
      console.error('Error updating user settings', error);
    }
  },
);

export const updateLayoutSettings = createAsyncThunk<
  void,
  { update: Partial<UserSettings['layoutSettings']>; shouldDebounceSave?: boolean },
  { state: MerchantAdminState }
>('userSettings/updateLayoutSettings', async ({ update, shouldDebounceSave = false }, { getState, dispatch }) => {
  const state = getState().userSettings;
  const nextLayoutSettings = { ...state.layoutSettings, ...update };
  dispatch(setUserSettings({ ...state, layoutSettings: nextLayoutSettings }));

  if (!shouldDebounceSave) {
    dispatch(saveUserSettings());
    return;
  }

  if (layoutSettingsSaveTimeoutId !== null) window.clearTimeout(layoutSettingsSaveTimeoutId);
  layoutSettingsSaveTimeoutId = window.setTimeout(() => {
    dispatch(saveUserSettings());
    layoutSettingsSaveTimeoutId = null;
  }, 250);
});

export const updateAiSettings = createAsyncThunk<
  void,
  { update: Partial<AiSettings>; shouldDebounceSave?: boolean },
  { state: MerchantAdminState }
>('userSettings/updateAiSettings', async ({ update, shouldDebounceSave = false }, { getState, dispatch }) => {
  const state = getState().userSettings;
  const nextAiSettings = { ...(state.aiSettings ?? defaultAiSettings), ...update };
  dispatch(setUserSettings({ ...state, aiSettings: nextAiSettings }));

  if (!shouldDebounceSave) {
    dispatch(saveUserSettings());
    return;
  }

  if (aiSettingsSaveTimeoutId !== null) window.clearTimeout(aiSettingsSaveTimeoutId);
  aiSettingsSaveTimeoutId = window.setTimeout(() => {
    dispatch(saveUserSettings());
    aiSettingsSaveTimeoutId = null;
  }, 250);
});

export const updateTableSettings = createAsyncThunk<
  void,
  { component: string; update: Partial<TableSettings> },
  { state: MerchantAdminState }
>('userSettings/update', async ({ component, update }, { getState, dispatch }) => {
  try {
    const state = getState().userSettings;
    const updatedSettings = {
      ...state.tableSettings?.[component],
      ...update,
    };
    dispatch(
      setUserSettings({
        ...state,
        tableSettings: {
          ...state.tableSettings,
          [component]: updatedSettings,
        },
      }),
    );
    dispatch(saveUserSettings());
  } catch (error) {
    console.error('Error saving user settings', error);
  }
});

export const clearTableSettings = createAsyncThunk<
  void,
  { component: string; customDefaultSettings?: Partial<TableSettings> },
  { state: MerchantAdminState }
>('userSettings/clear', async ({ component, customDefaultSettings }, { getState, dispatch }) => {
  const state = getState().userSettings;
  const settings = customDefaultSettings ? { ...defaultGridSettings, ...customDefaultSettings } : defaultGridSettings;

  dispatch(
    setUserSettings({
      ...state,
      tableSettings: {
        ...state.tableSettings,
        [component]: settings,
      },
    }),
  );
  dispatch(saveUserSettings());
});

export const setUserEmail = createAsyncThunk<void, string, { state: MerchantAdminState }>(
  'userSettings/setEmail',
  async (email: string, { dispatch }) => {
    try {
      await userSettingsAPI.setEmail(email);
      dispatch(showSnackbar(i18n.t('userSettings.emailUpdatedSuccessfully'), { severity: 'success' }));
    } catch (error) {
      const err = toPayerError((error as any) ?? {});
      dispatch(showSnackbar(err.message || i18n.t('userSettings.emailUpdateFailed'), { severity: 'error' }));
    }
  },
);

export const userSettingsSlice = createSlice({
  name: 'userSettings',
  initialState,
  reducers: {
    setUserSettings: (state, action: PayloadAction<Partial<UserSettings>>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(loadUserSettings.fulfilled, (state, action) => {
      state.isLoaded = true;
    });
    builder.addCase(loadUserSettings.rejected, (state, action) => {
      console.error('Error loading user settings:', action.error);
      console.log('Default user settings will be used');
      return { ...initialState, isLoaded: true };
    });
  },
});

const { setUserSettings } = userSettingsSlice.actions;
export const { reducer: userSettingsReducer } = userSettingsSlice;

export const selectGridSettings = (state: MerchantAdminState, gridId: string): TableSettings =>
  state.userSettings?.tableSettings?.[gridId] ?? defaultGridSettings;
